/**
 * ZIP Blob (runPipeline の戻り値) → MP4 Blob のオーケストレータ。
 *
 * 責務:
 *   1. ZIP を JSZip で展開し、ファイル名から hero/通常を判別
 *   2. カーセンサー命名規則 (01_hero.png / 02.jpg..80.jpg) を前提に順序付け
 *   3. VideoSlide[] を組み立て (hero=4秒、他=2秒)
 *   4. ロゴ PNG を fetch で取得 (失敗時はロゴなしで続行)
 *   5. ffmpeg.wasm をロードして buildVideo 実行
 *   6. 進捗はフェーズ毎に合算して呼び出し側に通知
 */

import JSZip from 'jszip';

import { loadFFmpeg } from './ffmpeg-loader';
import { type BuildVideoResult, type VideoProgress, type VideoSlide, VIDEO_SPEC } from './types';
import { buildVideo, type FFmpegHandle } from './video-builder';

export interface BuildVideoFromZipParams {
  readonly zipBlob: Blob;
  readonly outputName: string;
  readonly onProgress?: (p: VideoProgress & { overallRatio: number }) => void;
  /** テスト用に DI: 既にロード済みの ffmpeg ハンドル */
  readonly handle?: FFmpegHandle;
  /** テスト用に DI: ロゴ Blob (デフォルトは /hitop_logo.png を fetch) */
  readonly logo?: Blob | null;
}

/**
 * ZIP Blob から MP4 Blob を生成する。
 *
 * フェーズと全体進捗の割り当て:
 *   - unzip           : 0 〜 10%
 *   - fetch logo      : 10 〜 12%
 *   - load ffmpeg     : 12 〜 35%
 *   - write inputs    : 35 〜 45%
 *   - encoding        : 45 〜 95%
 *   - read output     : 95 〜 100%
 */
export async function buildVideoFromZip(
  params: BuildVideoFromZipParams,
): Promise<BuildVideoResult> {
  const report = (inner: VideoProgress, base: number, span: number): void => {
    const overall = Math.min(1, Math.max(0, base + inner.ratio * span));
    params.onProgress?.({ ...inner, overallRatio: overall });
  };

  // 1. ZIP 展開
  report({ phase: 'writing-inputs', ratio: 0, message: 'ZIP 解凍中' }, 0, 0.1);
  const slides = await extractSlidesFromZip(params.zipBlob);
  report({ phase: 'writing-inputs', ratio: 1, message: 'ZIP 解凍完了' }, 0, 0.1);

  // 2. ロゴ取得 (fetch 失敗時は undefined で続行)
  let logo: Blob | undefined;
  if (params.logo === null) {
    logo = undefined;
  } else if (params.logo !== undefined) {
    logo = params.logo;
  } else {
    report({ phase: 'writing-inputs', ratio: 0, message: 'ロゴ取得中' }, 0.1, 0.02);
    logo = await fetchLogoOrUndefined();
    report({ phase: 'writing-inputs', ratio: 1, message: 'ロゴ取得完了' }, 0.1, 0.02);
  }

  // 3. ffmpeg ロード (既に渡されていればスキップ)
  let handle = params.handle;
  if (!handle) {
    handle = await loadFFmpeg({
      onProgress: (r) =>
        report({ phase: 'loading-ffmpeg', ratio: r, message: 'ffmpeg 準備中' }, 0.12, 0.23),
    });
  } else {
    report({ phase: 'loading-ffmpeg', ratio: 1, message: 'ffmpeg 準備済み' }, 0.12, 0.23);
  }

  // 4. buildVideo 実行
  const result = await buildVideo(
    {
      slides,
      ...(logo !== undefined ? { logo } : {}),
      outputName: params.outputName,
    },
    {
      handle,
      onProgress: (inner) => {
        // フェーズごとのバンド割り当て
        if (inner.phase === 'writing-inputs') {
          report(inner, 0.35, 0.1);
        } else if (inner.phase === 'encoding') {
          report(inner, 0.45, 0.5);
        } else if (inner.phase === 'reading-output') {
          report(inner, 0.95, 0.05);
        } else if (inner.phase === 'done') {
          report(inner, 1, 0);
        }
      },
    },
  );

  return result;
}

// ===== 内部ヘルパ =======================================================

/**
 * ZIP から画像エントリを順序付きで取り出す。
 *
 * 命名規則 (CARSENSOR_SPEC):
 *   - `01_hero.png`: 先頭 (4 秒)
 *   - `NN.jpg`: 2 枚目以降 (2 秒)、NN は 02〜80
 *
 * `captions.csv` 等画像以外のエントリは無視。
 */
async function extractSlidesFromZip(zipBlob: Blob): Promise<VideoSlide[]> {
  const zip = await JSZip.loadAsync(zipBlob);
  const entries: { filename: string; blob: Blob }[] = [];
  for (const [filename, entry] of Object.entries(zip.files)) {
    if (entry.dir) continue;
    if (!/\.(png|jpg|jpeg)$/i.test(filename)) continue;
    // JSZip は entry.async('uint8array') で直接バイト列を返せるので、
    // Blob.arrayBuffer() (jsdom で未実装) に依存しない。
    const bytes = await entry.async('uint8array');
    const mime = filename.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
    // TS の Uint8Array<ArrayBufferLike> ↔ BlobPart の不整合を as でブリッジ
    const typed = new Blob([bytes as unknown as BlobPart], { type: mime });
    entries.push({ filename, blob: typed });
  }
  // 命名規則に基づいて順序付け
  entries.sort((a, b) => extractOrderKey(a.filename) - extractOrderKey(b.filename));

  if (entries.length === 0) {
    throw new Error('ZIP に動画化する画像が見つかりません');
  }

  // Wave 20: ffmpeg のメモリ不足対策として、各画像を事前に
  // VIDEO_SPEC.PRE_SCALE_WIDTH × PRE_SCALE_HEIGHT 以下に縮小してから渡す。
  // OffscreenCanvas が無い環境 (テスト等) は元 Blob をそのまま使う。
  const downscaled = await Promise.all(
    entries.map(async (e, idx) => ({
      blob: await downscaleForVideo(e.blob),
      filename: e.filename,
      idx,
    })),
  );

  const slides: VideoSlide[] = downscaled.map((e) => ({
    blob: e.blob,
    durationSec:
      e.idx === 0 && /_hero\.png$/i.test(e.filename)
        ? VIDEO_SPEC.HERO_DURATION_SEC
        : VIDEO_SPEC.DEFAULT_DURATION_SEC,
    label: e.filename,
  }));
  return slides;
}

/**
 * Wave 20: ffmpeg.wasm のメモリ逼迫を避けるため、入力画像を事前に 1280×960 以下に
 * 縮小する (元が ZIP 内で 2560×1920)。Canvas 描画 → JPEG q85 再エンコード。
 * OffscreenCanvas 不在環境 (jsdom 等) では元 Blob をそのまま返す。
 */
async function downscaleForVideo(blob: Blob): Promise<Blob> {
  if (typeof OffscreenCanvas === 'undefined' || typeof createImageBitmap === 'undefined') {
    return blob;
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return blob; // デコード失敗は諦めて元 Blob
  }
  const { PRE_SCALE_WIDTH: maxW, PRE_SCALE_HEIGHT: maxH } = VIDEO_SPEC;
  const scale = Math.min(maxW / bitmap.width, maxH / bitmap.height, 1);
  const outW = Math.max(1, Math.round(bitmap.width * scale));
  const outH = Math.max(1, Math.round(bitmap.height * scale));
  if (scale >= 1) {
    bitmap.close();
    return blob; // 既に十分小さい
  }
  const canvas = new OffscreenCanvas(outW, outH);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return blob;
  }
  ctx.drawImage(bitmap, 0, 0, outW, outH);
  bitmap.close();
  // PNG 入力 (hero) は PNG で維持 (透過情報)、JPEG 入力は JPEG q0.85
  const isPng = blob.type === 'image/png';
  return canvas.convertToBlob(
    isPng ? { type: 'image/png' } : { type: 'image/jpeg', quality: 0.85 },
  );
}

/**
 * ファイル名の先頭 2 桁 (または 01_hero) から順序キーを抽出。
 * hero は 1、2.jpg → 2, 80.jpg → 80。
 */
function extractOrderKey(filename: string): number {
  const m = filename.match(/^(\d{2})(?:_hero\.png|\.jpg|\.jpeg)$/i);
  if (m?.[1]) return parseInt(m[1], 10);
  // それ以外は末尾に回す (ZIP 汚染対策)
  return 9999;
}

/**
 * `/hitop_logo.png` を fetch。失敗しても投げずに undefined を返す
 * (ロゴなしで動画生成を継続するため)。
 */
async function fetchLogoOrUndefined(): Promise<Blob | undefined> {
  try {
    const res = await fetch('/hitop_logo.png');
    if (!res.ok) return undefined;
    const blob = await res.blob();
    return new Blob([await blob.arrayBuffer()], { type: 'image/png' });
  } catch {
    return undefined;
  }
}
